/*
 * The hamburger drawer menu (header-drawer / menu-drawer, from Dawn's
 * global.js) only adds an `overflow-hidden-*` class to <body> while
 * open. Confirmed live that this is NOT enough: <html> keeps its own
 * overflow-y: auto, so the page behind the drawer still scrolls with
 * the mouse wheel / touch, revealing the hero image underneath and
 * moving the announcement bar + header out of view even though the
 * drawer panel itself is open and correctly sized.
 *
 * cart-drawer.js already solved the identical problem for the cart
 * drawer with a real scroll lock (pin <body> with position: fixed and
 * restore the scroll position on close). This does the same thing for
 * the menu drawer by wrapping Dawn's own open/close methods instead of
 * editing the vendored global.js.
 *
 * The header is now a STICKY element (sticky_header_type: always).
 * Pinning <body> to position: fixed breaks position: sticky on its
 * descendants - the header's scrolling ancestor is no longer actually
 * scrolling, so it stops being "stuck" and snaps back to its normal
 * in-flow document position, which after scrolling down is far above
 * the viewport. Confirmed live: this is exactly "de bar verdwijnt en
 * alles staat vast" - the header visually disappears, and because
 * Dawn's own openMenuDrawer sizes the drawer from the header's
 * on-screen bottom edge, doing that measurement AFTER the header has
 * already jumped off-screen also breaks the drawer's height.
 *
 * Fixed by: (1) calling Dawn's original openMenuDrawer FIRST, while
 * the page is still normally scrolled and the header is genuinely
 * stuck at the top, so its measurement is correct; (2) explicitly
 * forcing the header to position: fixed; top: 0 for the duration the
 * drawer is open, so it stays visually pinned regardless of what
 * happens to <body>'s positioning; (3) restoring the header's own
 * inline styles on close so normal position: sticky resumes.
 */
/*
 * The nav list (Home / Dresses / Knitwear / ... / Track order) is much
 * shorter than the drawer panel, which is now a full 100vh (see
 * isadora-brand.css - needed so the drawer + its blur overlay also
 * cover the header/sale banner above it). A fixed rem-based top
 * padding only looked right at one specific window height: on a
 * shorter window it left a dead gap below "Track order", and on a
 * taller window (confirmed live at 1250px) the SAME fixed value left
 * a huge, mostly-empty white area above "Home" instead - because the
 * list's own content height doesn't scale with the viewport but a
 * fixed rem value doesn't adapt either.
 *
 * Fixed by measuring the real available height at open time and
 * computing the spacing to match: a small, capped gap above "Home",
 * with the rest of any leftover space distributed as extra breathing
 * room BETWEEN items (not as a single dead zone), so the list still
 * ends flush with the bottom of the drawer regardless of window size.
 */
function isaAdjustMenuDrawerSpacing() {
  var nav = document.querySelector('.menu-drawer__navigation');
  var list = nav ? nav.querySelector('ul') : null;
  if (!nav || !list || !list.children.length) return;

  var items = Array.prototype.slice.call(list.children);

  /* isadora-brand.css has pre-existing rules from earlier in the
     project - ".menu-drawer__navigation { padding: 0 0 40px !important }"
     and ".menu-drawer__menu > li { margin: 0 !important }" - that beat
     a plain inline style every time (an !important stylesheet rule
     always wins over a non-important inline style, regardless of
     source order). Confirmed live: the computed padding-top/margin-
     bottom stayed 0 even with the inline values set below. Using
     setProperty(..., 'important') makes these genuinely win. */
  nav.style.removeProperty('padding-top');
  items.forEach(function (li) {
    li.style.removeProperty('margin-bottom');
  });

  var navHeight = nav.getBoundingClientRect().height;
  var listHeight = list.getBoundingClientRect().height;
  var bottomPad = parseFloat(getComputedStyle(nav).paddingBottom) || 0;
  var capTop = 55; // px - a little breathing room above "Home", capped
  var available = navHeight - bottomPad;
  var leftover = available - capTop - listHeight;

  if (leftover > 0 && items.length > 1) {
    var perGap = leftover / (items.length - 1);
    items.forEach(function (li, i) {
      if (i === items.length - 1) return;
      var current = parseFloat(getComputedStyle(li).marginBottom) || 0;
      li.style.setProperty('margin-bottom', (current + perGap) + 'px', 'important');
    });
  }

  nav.style.setProperty('padding-top', capTop + 'px', 'important');
}

document.addEventListener('DOMContentLoaded', function () {
  var HeaderDrawer = customElements.get('header-drawer');
  if (!HeaderDrawer) return;

  var originalOpen = HeaderDrawer.prototype.openMenuDrawer;
  var originalClose = HeaderDrawer.prototype.closeMenuDrawer;

  HeaderDrawer.prototype.openMenuDrawer = function () {
    var result = originalOpen.apply(this, arguments);

    isaAdjustMenuDrawerSpacing();

    this.isaMenuScrollLockY = window.scrollY || window.pageYOffset || 0;

    var headerEl = this.header || document.querySelector('.section-header');
    if (headerEl) {
      this.isaHeaderPrevStyle = {
        position: headerEl.style.position,
        top: headerEl.style.top,
        left: headerEl.style.left,
        width: headerEl.style.width
      };
      headerEl.style.position = 'fixed';
      headerEl.style.top = '0';
      headerEl.style.left = '0';
      headerEl.style.width = '100%';
    }

    document.body.style.position = 'fixed';
    document.body.style.top = '-' + this.isaMenuScrollLockY + 'px';
    document.body.style.width = '100%';

    return result;
  };

  HeaderDrawer.prototype.closeMenuDrawer = function () {
    /* Dawn's own closeMenuDrawer (HeaderDrawer override) does
       "if (!elementToFocus) return" BEFORE any cleanup - some of
       Dawn's own callers (e.g. onFocusOut) call this.closeMenuDrawer()
       with no arguments at all, which hits that guard and skips
       everything, including removing the "menu-open" class Dawn adds
       to the header in openMenuDrawer. Our own lock/restore below must
       run regardless of whether Dawn's original function actually did
       anything, so it's unconditional here rather than gated on the
       result. */
    var result = originalClose.apply(this, arguments);

    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.width = '';

    var headerEl = this.header || document.querySelector('.section-header');
    if (headerEl) {
      headerEl.classList.remove('menu-open');
      if (this.isaHeaderPrevStyle) {
        headerEl.style.position = this.isaHeaderPrevStyle.position;
        headerEl.style.top = this.isaHeaderPrevStyle.top;
        headerEl.style.left = this.isaHeaderPrevStyle.left;
        headerEl.style.width = this.isaHeaderPrevStyle.width;
        this.isaHeaderPrevStyle = null;
      }
      /* Toggling position: fixed -> sticky back and forth on the same
         element via inline styles left position: sticky computing
         correctly (getComputedStyle says "sticky") but NOT actually
         re-engaging - confirmed live, the header kept scrolling away
         as if static until a real reflow was forced. Reading
         offsetHeight forces the browser to redo layout synchronously
         with the restored style, which fixes it. */
      void headerEl.offsetHeight;
    }

    if (typeof this.isaMenuScrollLockY === 'number') {
      window.scrollTo(0, this.isaMenuScrollLockY);
      this.isaMenuScrollLockY = null;
    }
    return result;
  };
});
